import type { IEventChannel } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type {ApiOpts} from "@esWebAPI/es/model/Models";
import type CacheModule from "./CacheModule";

export default class BucketModule {

    private readonly BUCKET_TIMEOUT = (5 * 60 * 1000) - (5 * 1000); // 4min 55s, the cache is invalidated 5 sec before the server, just in case.
    private readonly MAX_BUCKETS = 25;

    private readonly eventChannel: IEventChannel;
    private readonly rqBroker: AjaxRequestBroker;
    private readonly resourceName: string;
    private readonly cache: CacheModule;

    private readonly dfdMap = {};
    private readonly timeoutMap = {};
    private readonly queue = [];
    private readonly pendingBucketFromServerDfd = {};

    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.cache = cache;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;

        eventChannel.addListener("onDestroy", function(){
            eventChannel = null; // prevent memory leaks when the map widget is destroyed
        });
    }

    private cacheBucket (key, dfd) {
        this.dfdMap[key] = dfd;
        this.timeoutMap[key] = window.setTimeout(() => { // delete cache after X minutes
            delete this.dfdMap[key];
            delete this.timeoutMap[key];
        }, this.BUCKET_TIMEOUT);

        this.queue.push(key);

        if (this.queue.length > this.MAX_BUCKETS) {
            const last = this.queue.shift();
            delete this.dfdMap[last];
            const timer = this.timeoutMap[last];
            window.clearTimeout(timer);
            delete this.timeoutMap[last];
        }
    }

    /**
     * @function es.module.[resource].post
     * POST a new resource to the server (note: id attrib must be -1)
     * @param data e.g. {"id":-1,"region":"","title":"US States (Contiguous)","description":"US States (Contiguous)","layerMetaId":102,"profile":1}]
     * @param opts
     * @return a jquery deferred object resolved when the request has completed
     */
    public store (data, opts?: ApiOpts) {
        const dfd = this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName),
            opts: opts,
            data: "data=" + encodeURIComponent(data),
            contentType: 'application/x-www-form-urlencoded'
        });

        return dfd.done(bucket => {
            this.cacheBucket(bucket.key, dfd);
            this.cache.set(this.resourceName, bucket.key, bucket);
        });
    }

    /**
     * Retrieve the Deferred to create a bucket. This will act as a read.
     * Reading a bucket will cause a reset of the timeout, and push the bucket as the last to be deleted.
     *
     * @param key the hash of the request (or bucket key)
     * @returns {*}
     */
    private getBucketDfd (key) {
        const dfd = this.dfdMap[key];
        if (!dfd) {
            return null;
        }

        const timer = this.timeoutMap[key];

        if (!timer) {
            es.log.error("Could not find timer for ", key);
        }

        window.clearTimeout(timer);

        const index = this.queue.indexOf(key);
        this.queue.splice(index, 1);
        this.cacheBucket(key, dfd);

        return dfd;
    }

    /**
     * Before calling store, check if there is a pending request to store the same bucket in progress.
     * If there is then return that deferred
     * If not, then create new deferred and put it in the pending list.
     * Delete from the pending list when done
     */
    private createOrGetPending(key, bucketParams){
        if (this.pendingBucketFromServerDfd[key]){
            return this.pendingBucketFromServerDfd[key];
        }

        return this.pendingBucketFromServerDfd[key] = this.store(bucketParams).always(() => {
            delete this.pendingBucketFromServerDfd[key];
        });
    }

    /**
     * Try to retrieve the bucket if already exist, or create a new bucket.
     * Retrieving a bucket will cause a reset of the timeout, and push the bucket as the last to be deleted.
     *
     * @param key the hash of the request (or bucket key)
     * @param bucketParams params to create the bucket.
     * @returns {*}
     */
    public getOrCreateBucket(key, bucketParams) {
        return this.getBucketDfd(key) || this.createOrGetPending(key, bucketParams);
    }
}